import {
  type Centrifuge,
  type Subscription,
  UnauthorizedError,
  type ConnectedContext,
  type DisconnectedContext,
  type TransportName,
} from 'centrifuge'
import { inject, injectable } from 'inversify'
import { getWorkspaceChannelName, type SocketTokenGettingCallback } from '@/services/transport/types'
import TmLogicError from '@/core/error/tmLogicError'
import { buildEmulationEndpoint, buildTransport } from '@/utils/centrifuge'
import { SERVICE_TYPES } from '@/core/container/types'
import type CentrifugeClient from '@/services/transport/centrifugeClient'
import type { Config } from '@/core/types'
import type UserSettingsService from '@/services/domain/user/userSettingsService'
import type CentrifugeService from '@/services/domain/accounts/CentrifugeService'
import type UserService from '@/services/domain/user/userService'

export interface CentrifugeConnectionOnPublicationContext {
  data?: unknown
}

export type BaseCentrifuge = Pick<
  Centrifuge,
  'state' | 'on' | 'removeAllListeners' | 'publish' | 'connect' | 'disconnect'
>

export class CentrifugeConnection {
  constructor(
    protected readonly centrifuge: BaseCentrifuge,
    protected getChannelName: () => string,
  ) {}

  public get state() {
    return this.centrifuge.state
  }

  public onDisconnected(listener: (ctx: DisconnectedContext) => void) {
    this.centrifuge.on('disconnected', listener)
  }

  public onConnected(listener: (ctx: ConnectedContext) => void) {
    this.centrifuge.on('connected', listener)
  }

  /**
   *
   * @virtual
   */
  public onPublication(listener: (ctx: CentrifugeConnectionOnPublicationContext) => void) {
    this.centrifuge.on('publication', listener)
  }

  public removeAllListeners() {
    return this.centrifuge.removeAllListeners()
  }

  public publish(eventName: string, payload: unknown) {
    return this.centrifuge.publish(this.getChannelName(), {
      type: eventName,
      payload,
    })
  }

  public connect() {
    return this.centrifuge.connect()
  }

  public disconnect() {
    return this.centrifuge.disconnect()
  }
}

class CentrifugeConnectionForMainServerSubscription extends CentrifugeConnection {
  constructor(
    protected readonly centrifuge: BaseCentrifuge,
    private readonly userService: UserService,
    protected getChannelName: () => string,
  ) {
    super(centrifuge, getChannelName)
  }

  public override onPublication(listener: (ctx: CentrifugeConnectionOnPublicationContext) => void): void {
    this.centrifuge.on('publication', (e) => {
      const event = e.data
      if (!event || !event.type) {
        return
      }
      // This may be related to the task on the backend https://textmagic.atlassian.net/browse/TM-2118
      // Just do not remove it
      if ('user_id' in event && event.user_id!.toString() !== this.userService.currentUserId()) {
        return
      }
      listener(e)
    })
  }
}

class CentrifugeConnectionWithSubscription extends CentrifugeConnection {
  constructor(
    protected readonly centrifuge: BaseCentrifuge,
    protected subscription: Subscription,
    protected getChannelName: () => string,
  ) {
    super(centrifuge, getChannelName)
  }

  public override onPublication(listener: (ctx: CentrifugeConnectionOnPublicationContext) => void): void {
    this.subscription.on('publication', listener)
  }
}

@injectable()
export default class ServerEventsSubscriptionConnectionBuilderService {
  constructor(
    @inject(SERVICE_TYPES.UserService) private readonly userService: UserService,
    @inject(SERVICE_TYPES.UserSettingsService) private readonly userSettingsService: UserSettingsService,
    @inject(SERVICE_TYPES.CentrifugeClient) protected readonly centrifugeClient: CentrifugeClient,
    @inject(SERVICE_TYPES.CentrifugeService) protected readonly centrifugeService: CentrifugeService,
    @inject(SERVICE_TYPES.Config) protected readonly config: Config,
  ) {}

  private getSocketUrl() {
    return this.config.socketUrl
  }

  public async createConnectionForMainServerSubscription() {
    const baseUrl = this.getSocketUrl() || import.meta.env.VUE_APP_WIRE_URL
    const transports: TransportName[] = ['websocket', 'http_stream', 'sse']
    const endpoints = transports.map((transport) => buildTransport(baseUrl, transport))
    const needsEmulation = transports.some((t) => t === 'http_stream' || t === 'sse')
    const centrifuge = this.centrifugeClient.create(endpoints, {
      emulationEndpoint: needsEmulation ? buildEmulationEndpoint(baseUrl) : undefined,
    })
    return new CentrifugeConnectionForMainServerSubscription(centrifuge, this.userService, () => {
      throw new TmLogicError('Not implemented "getChannelName" callback')
    })
  }

  public createConnectionForWorkspaceServerSubscription() {
    const { parentId } = this.userSettingsService.currentUserSettingsOrFail()
    const getChannelName = () => {
      return getWorkspaceChannelName(parentId)
    }
    return this.createConnectionWithSubscription(getChannelName, () => {
      const channelName = getChannelName()
      return this.centrifugeService.getTokens([channelName])
    })
  }

  public async createConnectionWithSubscription(
    getChannelName: () => string,
    getTokens: SocketTokenGettingCallback,
  ): Promise<CentrifugeConnection> {
    const tokens = await getTokens({})

    const centrifuge = this.centrifugeClient.create(this.getSocketUrl(), {
      token: tokens.connectionToken,
      getToken: async () => {
        const { connectionToken } = await getTokens({ isTokenRefreshing: true })
        if (!connectionToken) {
          throw new UnauthorizedError('No token provided')
        }
        return connectionToken
      },
    })
    const sub = centrifuge.newSubscription(tokens.subscriptionTokens[0].channel, {
      token: tokens.subscriptionTokens[0].subscriptionToken,
      getToken: async () => {
        const { subscriptionTokens } = await getTokens({ isTokenRefreshing: true })
        if (!subscriptionTokens.length || !subscriptionTokens[0].subscriptionToken) {
          throw new UnauthorizedError('No token provided')
        }
        return subscriptionTokens[0].subscriptionToken
      },
    })
    sub.subscribe()

    return new CentrifugeConnectionWithSubscription(centrifuge, sub, getChannelName)
  }
}
